/**
 * Multi-node align / distribute / tidy-up geometry — the Figma "align bar"
 * affordance. Pure functions: each takes the current doc + selected ids and
 * returns a new doc with x/y patched.
 *
 * Bounds come from getNodeAABB (rotation-aware, scene space). We only ever
 * apply translation deltas, and a translation in scene space equals the same
 * delta on a node's parent-relative x/y (offset-invariant) — so nesting is
 * handled without resolving parent offsets, matching how drag/snap already
 * operate. Nodes governed by an auto-layout parent are still re-laid-out by the
 * solver on the next pass; align/distribute is meant for free-positioned nodes.
 */
import { getNodeAABB } from './canvaskit-hit-test';
import { updateNodes } from './canvaskit-mutations';

import type { SceneDocument, SceneNode } from '@/lib/scene-document';

export type AlignEdge = 'left' | 'hcenter' | 'right' | 'top' | 'vcenter' | 'bottom';
export type DistributeAxis = 'horizontal' | 'vertical';

interface Box {
  id: string;
  x: number;
  y: number;
  width: number;
  height: number;
}

function collectBoxes(doc: SceneDocument, ids: Iterable<string>): Box[] {
  const boxes: Box[] = [];
  for (const id of ids) {
    if (!doc.nodes[id]) continue;
    const a = getNodeAABB(doc, id);
    boxes.push({ id, x: a.x, y: a.y, width: a.width, height: a.height });
  }
  return boxes;
}

function applyDeltas(
  doc: SceneDocument,
  deltas: Map<string, { dx: number; dy: number }>,
): SceneDocument {
  const patches = new Map<string, Partial<SceneNode>>();
  for (const [id, { dx, dy }] of deltas) {
    if (dx === 0 && dy === 0) continue;
    const node = doc.nodes[id];
    if (!node) continue;
    patches.set(id, { x: node.x + dx, y: node.y + dy });
  }
  return updateNodes(doc, patches);
}

/** Align the selection's AABBs to a shared edge/center of the selection bounds. */
export function alignNodes(doc: SceneDocument, ids: Iterable<string>, edge: AlignEdge): SceneDocument {
  const boxes = collectBoxes(doc, ids);
  if (boxes.length < 2) return doc;

  const minX = Math.min(...boxes.map((b) => b.x));
  const maxX = Math.max(...boxes.map((b) => b.x + b.width));
  const minY = Math.min(...boxes.map((b) => b.y));
  const maxY = Math.max(...boxes.map((b) => b.y + b.height));
  const cx = (minX + maxX) / 2;
  const cy = (minY + maxY) / 2;

  const deltas = new Map<string, { dx: number; dy: number }>();
  for (const b of boxes) {
    let dx = 0;
    let dy = 0;
    switch (edge) {
      case 'left': dx = minX - b.x; break;
      case 'hcenter': dx = cx - (b.x + b.width / 2); break;
      case 'right': dx = maxX - (b.x + b.width); break;
      case 'top': dy = minY - b.y; break;
      case 'vcenter': dy = cy - (b.y + b.height / 2); break;
      case 'bottom': dy = maxY - (b.y + b.height); break;
    }
    deltas.set(b.id, { dx, dy });
  }
  return applyDeltas(doc, deltas);
}

/**
 * Distribute the selection so the gaps between adjacent AABBs are equal
 * (Figma "distribute spacing"). The two extreme items stay put. Needs 3+.
 */
export function distributeNodes(doc: SceneDocument, ids: Iterable<string>, axis: DistributeAxis): SceneDocument {
  const boxes = collectBoxes(doc, ids);
  if (boxes.length < 3) return doc;

  const horizontal = axis === 'horizontal';
  const start = (b: Box) => (horizontal ? b.x : b.y);
  const size = (b: Box) => (horizontal ? b.width : b.height);

  const sorted = [...boxes].sort((a, b) => start(a) - start(b));
  const first = sorted[0];
  const last = sorted[sorted.length - 1];
  const span = start(last) + size(last) - start(first); // outer extent
  const totalSize = sorted.reduce((sum, b) => sum + size(b), 0);
  const gap = (span - totalSize) / (sorted.length - 1);

  const deltas = new Map<string, { dx: number; dy: number }>();
  let cursor = start(first);
  for (const b of sorted) {
    const target = cursor;
    const delta = target - start(b);
    deltas.set(b.id, horizontal ? { dx: delta, dy: 0 } : { dx: 0, dy: delta });
    cursor += size(b) + gap;
  }
  return applyDeltas(doc, deltas);
}

/**
 * Tidy up: snap a loose selection into a clean line. Picks the dominant axis
 * (wider-than-tall -> row, else column), equal-distributes along it, and aligns
 * centers on the cross axis. A pragmatic subset of Figma's grid tidy-up that
 * covers the common "clean up this row/column" case.
 */
export function tidyUpNodes(doc: SceneDocument, ids: Iterable<string>): SceneDocument {
  const boxes = collectBoxes(doc, ids);
  if (boxes.length < 2) return doc;

  const minX = Math.min(...boxes.map((b) => b.x));
  const maxX = Math.max(...boxes.map((b) => b.x + b.width));
  const minY = Math.min(...boxes.map((b) => b.y));
  const maxY = Math.max(...boxes.map((b) => b.y + b.height));
  const rowLike = maxX - minX >= maxY - minY;

  let next = alignNodes(doc, ids, rowLike ? 'vcenter' : 'hcenter');
  if (boxes.length >= 3) {
    next = distributeNodes(next, ids, rowLike ? 'horizontal' : 'vertical');
  }
  return next;
}
